import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { fbTrack } from "@/lib/siteConfig";

const POLL_MS = 3000;
const MAX_POLLS = 60;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const orderId = params.get("order") || localStorage.getItem("pz_pending_order");
  const [state, setState] = useState(orderId ? "checking" : "missing");
  const [info, setInfo] = useState(null);
  const [left, setLeft] = useState(5);
  const polls = useRef(0);

  useEffect(() => {
    if (!orderId) return;
    let timer;
    const check = async () => {
      polls.current += 1;
      try {
        const { data } = await api.get(`/wallet/gateway/status/${orderId}`);
        setInfo(data);
        if (data.status === "success") {
          localStorage.removeItem("pz_pending_order");
          window.dispatchEvent(new Event("wallet:refresh"));
          const key = `pz_px_${orderId}`;
          if (!localStorage.getItem(key)) {
            localStorage.setItem(key, "1");
            fbTrack("deposit", { value: data.amount, currency: "INR", content_name: "wallet_recharge" }, orderId);
          }
          return setState("success");
        }
        if (data.status === "failed") { localStorage.removeItem("pz_pending_order"); return setState("failed"); }
      } catch (e) {
        if (e.response?.status === 404) { localStorage.removeItem("pz_pending_order"); return setState("missing"); }
      }
      if (polls.current >= MAX_POLLS) return setState("timeout");
      timer = setTimeout(check, POLL_MS);
    };
    check();
    return () => clearTimeout(timer);
  }, [orderId]);

  useEffect(() => {
    if (state !== "success") return;
    if (left <= 0) { nav("/home", { replace: true }); return; }
    const t = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(t);
  }, [state, left, nav]);

  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="payment-return-page">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl pz-glass border border-white/10 p-8 text-center">
        {state === "checking" && (<>
          <Loader2 className="h-14 w-14 text-pazer-red animate-spin mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl mb-2" data-testid="payment-checking">Confirming your payment…</h1>
          <p className="text-white/50 text-sm">Please don't close this page. This usually takes a few seconds.</p>
        </>)}
        {state === "success" && (<>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}>
            <CheckCircle2 className="h-20 w-20 text-green-400 mx-auto mb-4" />
          </motion.div>
          <h1 className="font-display font-extrabold text-3xl mb-2" data-testid="payment-thankyou">Thank you for recharge!</h1>
          <p className="text-green-400 font-bold text-lg mb-1" data-testid="payment-coins">+{Math.round(info?.coins || 0).toLocaleString()} coins added</p>
          {info?.bonus > 0 && <p className="text-yellow-300 font-bold text-sm mb-1" data-testid="payment-bonus">+{Math.round(info.bonus).toLocaleString()} first recharge bonus</p>}
          <p className="text-white/60 text-sm">New balance <span className="font-mono-pz text-white" data-testid="payment-new-balance">{info?.wallet?.total_balance?.toLocaleString()}</span></p>
          <p className="text-white/40 text-xs mt-6" data-testid="payment-redirect-countdown">Taking you back to the game in {left}s…</p>
        </>)}
        {(state === "failed" || state === "timeout" || state === "missing") && (<>
          <XCircle className="h-16 w-16 text-pazer-red mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl mb-2" data-testid="payment-failed">
            {state === "failed" ? "Payment not completed" : state === "missing" ? "No payment found" : "Still waiting for confirmation"}
          </h1>
          <p className="text-white/50 text-sm mb-6">
            {state === "timeout" ? "If money was debited, your coins will be added automatically once the bank confirms." : "No money was added. You can try again from your wallet."}
          </p>
          <button onClick={() => nav("/wallet", { replace: true })} data-testid="payment-back-wallet"
            className="w-full py-3 rounded-full bg-pazer-red hover:bg-pazer-redhover font-bold transition-colors">Go to Wallet</button>
        </>)}
      </motion.div>
    </div>
  );
}
